import React, { useState, useEffect } from 'react';
import { useWallet, useConnection } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { FiLink, FiTrendingUp, FiEye, FiExternalLink, FiCopy, FiCheck } from 'react-icons/fi';
import { PublicKey, Transaction, TransactionInstruction, SystemProgram } from '@solana/web3.js';
import { getAssociatedTokenAddress, getAccount, TOKEN_PROGRAM_ID, TOKEN_2022_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID, createAssociatedTokenAccountInstruction } from '@solana/spl-token';
import Shuffle from './Shuffle';
import axios from 'axios';
import './Home.css';

// Use relative path for production, or env variable if set
const API_URL = process.env.REACT_APP_API_URL || '';

function Home() {
  const { connected, publicKey, signTransaction } = useWallet();
  const { connection } = useConnection();
  const [ca, setCa] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [link, setLink] = useState('');
  const [topCA, setTopCA] = useState([]);
  const [loadingTop, setLoadingTop] = useState(true);
  const [latestCA, setLatestCA] = useState([]);
  const [loadingLatest, setLoadingLatest] = useState(true);
  const [copiedUuid, setCopiedUuid] = useState(null);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [features, setFeatures] = useState({
    top_ca_enabled: true,
    last_ca_enabled: true,
    my_ca_enabled: true,
  });

  useEffect(() => {
    loadFeatures();
    loadPaymentConfig();
  }, []);

  useEffect(() => {
    if (features.top_ca_enabled) {
      loadTopCA();
    }
    if (features.last_ca_enabled) {
      loadLatestCA();
    }
  }, [features]);

  const loadPaymentConfig = async () => {
    try {
      console.log('Loading payment config from:', `${API_URL}/api/payment-config`);
      const response = await axios.get(`${API_URL}/api/payment-config`);
      console.log('Payment config loaded:', response.data);
      setPaymentConfig(response.data);
    } catch (err) {
      console.error('Failed to load payment config:', err);
      setError('Failed to load payment configuration. Please refresh the page.');
    }
  };

  const loadFeatures = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/features`);
      setFeatures(response.data);
    } catch (err) {
      console.error('Failed to load features:', err);
      // Use defaults if failed to load
    }
  };

  const loadTopCA = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/top`);
      setTopCA(response.data);
    } catch (err) {
      console.error('Failed to load top CA:', err);
    } finally {
      setLoadingTop(false);
    }
  };

  const loadLatestCA = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/latest`);
      setLatestCA(response.data);
    } catch (err) {
      console.error('Failed to load latest CA:', err);
    } finally {
      setLoadingLatest(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLink('');

    if (!connected || !publicKey) {
      setError('Please connect your wallet first');
      return;
    }

    if (!ca.trim()) {
      setError('Please enter a CA address');
      return;
    }

    if (!paymentConfig || paymentConfig.enabled === false) {
      setError('Payment feature is not available yet. Coming soon!');
      return;
    }

    setLoading(true);
    try {
      console.log('Payment config:', paymentConfig);
      
      // Create payment transaction
      const tokenMint = new PublicKey(paymentConfig.token_mint);
      const destWallet = new PublicKey(paymentConfig.dest_wallet);
      const amount = parseInt(paymentConfig.amount);
      const decimals = parseInt(paymentConfig.decimals);

      console.log('Token mint:', paymentConfig.token_mint);
      console.log('Dest wallet:', paymentConfig.dest_wallet);
      console.log('Amount:', amount, 'Decimals:', decimals);

      // Check token type (Token or Token-2022) by checking mint account
      // Note: ASSOCIATED_TOKEN_PROGRAM_ID is the same for both Token and Token-2022
      let tokenProgramId = TOKEN_PROGRAM_ID;
      const associatedTokenProgramId = ASSOCIATED_TOKEN_PROGRAM_ID; // Same for both Token and Token-2022
      
      try {
        const mintAccountInfo = await connection.getAccountInfo(tokenMint);
        if (mintAccountInfo) {
          const mintProgramId = mintAccountInfo.owner.toString();
          console.log('Mint account owner (program ID):', mintProgramId);
          console.log('TOKEN_PROGRAM_ID:', TOKEN_PROGRAM_ID.toString());
          console.log('TOKEN_2022_PROGRAM_ID:', TOKEN_2022_PROGRAM_ID?.toString() || 'not imported');
          
          // Check if it's Token-2022
          if (TOKEN_2022_PROGRAM_ID && mintProgramId === TOKEN_2022_PROGRAM_ID.toString()) {
            tokenProgramId = TOKEN_2022_PROGRAM_ID;
            console.log('Using Token-2022 program ID');
          } else {
            console.log('Using standard Token program ID');
          }
        }
      } catch (err) {
        console.warn('Could not check mint account, using default Token program ID:', err);
        // Default to standard Token program ID
      }
      
      console.log('Using tokenProgramId:', tokenProgramId.toString());
      console.log('Using associatedTokenProgramId:', associatedTokenProgramId.toString());

      // Get associated token addresses with correct program IDs
      const sourceTokenAccount = await getAssociatedTokenAddress(
        tokenMint,
        publicKey,
        false, // allowOwnerOffCurve
        tokenProgramId,
        associatedTokenProgramId
      );

      const destTokenAccount = await getAssociatedTokenAddress(
        tokenMint,
        destWallet,
        false, // allowOwnerOffCurve
        tokenProgramId,
        associatedTokenProgramId
      );

      console.log('Source token account:', sourceTokenAccount.toString());
      console.log('Dest token account:', destTokenAccount.toString());

      // Calculate amount with decimals first
      const amountWithDecimals = amount * Math.pow(10, decimals);
      console.log('Amount with decimals:', amountWithDecimals);

      // Check token balance using connection.getParsedTokenAccountsByOwner
      // Note: getParsedTokenAccountsByOwner works with both Token and Token-2022
      try {
        const tokenAccounts = await connection.getParsedTokenAccountsByOwner(publicKey, {
          mint: tokenMint,
        });
        console.log('Token accounts found:', tokenAccounts.value.length);
        
        if (tokenAccounts.value.length === 0) {
          // Check all tokens to show what user has
          const allTokenAccounts = await connection.getParsedTokenAccountsByOwner(publicKey);
          console.log('All token accounts:', allTokenAccounts.value.length);
          if (allTokenAccounts.value.length > 0) {
            console.log('User has these tokens:');
            allTokenAccounts.value.forEach((account, index) => {
              const mint = account.account.data.parsed.info.mint;
              const balance = account.account.data.parsed.info.tokenAmount;
              console.log(`  ${index + 1}. Mint: ${mint}, Balance: ${balance.uiAmount} tokens`);
            });
          }
          const tokenAmount = paymentConfig.amount / 1000;
          throw new Error(`You don't have any ${tokenAmount}k $COPYCA tokens. Please acquire tokens first from pump.fun.`);
        }

        // Get balance from token account
        const tokenAccountInfo = tokenAccounts.value[0];
        const balance = tokenAccountInfo.account.data.parsed.info.tokenAmount;
        console.log('Token balance:', balance.uiAmount, 'tokens');
        console.log('Token balance (raw):', balance.amount);
        console.log('Token decimals:', balance.decimals);

        // eslint-disable-next-line no-undef
        const requiredAmount = BigInt(amountWithDecimals);
        // eslint-disable-next-line no-undef
        const currentBalance = BigInt(balance.amount);
        console.log('Required amount (raw):', requiredAmount.toString());
        console.log('Current balance (raw):', currentBalance.toString());

        if (currentBalance < requiredAmount) {
          throw new Error(`Insufficient balance. Required: ${amount} tokens, Available: ${balance.uiAmount} tokens`);
        }
        console.log('Balance check passed!');
      } catch (err) {
        if (err.message && (err.message.includes('Insufficient balance') || err.message.includes('Required:'))) {
          throw err;
        }
        // Check if it's a "not found" error - account doesn't exist
        if (err.message && (err.message.includes('TokenAccountNotFoundError') || err.message.includes('InvalidAccountData') || err.message.includes('not found') || err.message.includes('could not find account'))) {
          const tokenAmount = paymentConfig.amount / 1000;
          throw new Error(`You don't have any ${tokenAmount}k $COPYCA tokens. Please acquire tokens first from pump.fun.`);
        }
        console.error('Error checking source account:', err);
        const tokenAmount = paymentConfig.amount / 1000;
        throw new Error(`Cannot verify token balance. Please ensure you have ${tokenAmount}k $COPYCA tokens in your wallet. Error: ${err.message || err.toString()}`);
      }

      // Check if destination token account exists
      // Use correct token program ID for checking
      let destAccountExists = false;
      try {
        await getAccount(connection, destTokenAccount, 'confirmed', tokenProgramId);
        destAccountExists = true;
        console.log('Destination token account exists');
      } catch (err) {
        console.log('Destination token account does not exist, will create it');
        destAccountExists = false;
      }

      // Create transaction
      const transaction = new Transaction();

      // Add create ATA instruction only if account doesn't exist
      // Use correct program IDs based on token type
      if (!destAccountExists) {
        try {
          // Use library function with correct program IDs
          const createATAInstruction = createAssociatedTokenAccountInstruction(
            publicKey, // payer
            destTokenAccount, // associatedToken (ATA address)
            destWallet, // owner
            tokenMint, // mint
            tokenProgramId, // Use detected token program ID
            associatedTokenProgramId // Use detected associated token program ID
          );
          
          console.log('Create ATA instruction programId:', createATAInstruction.programId.toString());
          console.log('Create ATA instruction keys:', createATAInstruction.keys?.length);
          // Log all keys for debugging
          createATAInstruction.keys.forEach((key, index) => {
            console.log(`Key ${index}: ${key.pubkey.toString()}, signer: ${key.isSigner}, writable: ${key.isWritable}`);
          });
          console.log('Expected ATA address:', destTokenAccount.toString());
          console.log('Using tokenProgramId:', tokenProgramId.toString());
          console.log('Using associatedTokenProgramId:', associatedTokenProgramId.toString());
          
          transaction.add(createATAInstruction);
          console.log('Added create ATA instruction for:', destTokenAccount.toString());
        } catch (err) {
          console.error('Error creating ATA instruction:', err);
          throw new Error(`Failed to create ATA instruction: ${err.message}`);
        }
      }

      // Create transfer instruction manually (as per example)
      console.log('Using destination token account:', destTokenAccount.toString());
      
      try {
        // Transfer instruction format:
        // Instruction type: 3 (Transfer)
        // Amount: u64 little-endian (8 bytes)
        const instructionType = 3;
        // eslint-disable-next-line no-undef
        const amountBigInt = BigInt(amountWithDecimals);
        
        // Create instruction data: [instructionType (1 byte), amount (8 bytes little-endian)]
        const instructionData = new Uint8Array(9);
        instructionData[0] = instructionType;
        
        // Write amount as little-endian u64
        for (let i = 0; i < 8; i++) {
          // eslint-disable-next-line no-undef
          instructionData[1 + i] = Number((amountBigInt >> BigInt(i * 8)) & BigInt(0xff));
        }

        const transferInstruction = new TransactionInstruction({
          keys: [
            { pubkey: sourceTokenAccount, isSigner: false, isWritable: true }, // Source account
            { pubkey: destTokenAccount, isSigner: false, isWritable: true }, // Destination account
            { pubkey: publicKey, isSigner: true, isWritable: false }, // Owner (signer, but not writable)
          ],
          programId: tokenProgramId, // Use detected token program ID
          data: instructionData,
        });
        
        console.log('Transfer instruction keys:', transferInstruction.keys?.length);
        console.log('Transfer instruction programId:', transferInstruction.programId.toString());
        console.log('Transfer instruction data length:', instructionData.length);
        transaction.add(transferInstruction);
      } catch (err) {
        console.error('Error creating transfer instruction:', err);
        throw new Error(`Failed to create transfer instruction: ${err.message}`);
      }

      // Get recent blockhash and set transaction parameters
      const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = publicKey;

      console.log('Sending transaction...');
      console.log('Transaction instructions count:', transaction.instructions.length);
      console.log('Transaction feePayer:', transaction.feePayer?.toString());
      console.log('Transaction blockhash:', transaction.recentBlockhash);
      
      // Verify transaction is properly formed
      if (!transaction.feePayer) {
        throw new Error('Transaction feePayer is not set');
      }
      if (!transaction.recentBlockhash) {
        throw new Error('Transaction recentBlockhash is not set');
      }
      
      // Sign transaction first
      console.log('Signing transaction...');
      const signedTransaction = await signTransaction(transaction);
      console.log('Transaction signed');
      
      // Serialize signed transaction
      const serializedTx = signedTransaction.serialize();
      // Convert Uint8Array to base64 (browser-compatible)
      const txBase64 = btoa(String.fromCharCode(...serializedTx));
      console.log('Transaction serialized, length:', serializedTx.length);
      
      // Send signed transaction to backend
      console.log('Submitting signed transaction to backend...');
      const submitResponse = await axios.post(`${API_URL}/api/payment/submit`, {
        signed_transaction: txBase64,
      });
      
      const signature = submitResponse.data.signature;
      console.log('Transaction signature:', signature);
      
      console.log('Waiting for confirmation...');
      // Wait for confirmation
      let confirmed = false;
      let retries = 0;
      const maxRetries = 30; // 30 seconds max
      
      while (!confirmed && retries < maxRetries) {
        try {
          const status = await connection.getSignatureStatus(signature);
          if (status.value) {
            if (status.value.err) {
              throw new Error(`Transaction failed: ${JSON.stringify(status.value.err)}`);
            }
            if (status.value.confirmationStatus === 'confirmed' || status.value.confirmationStatus === 'finalized') {
              confirmed = true;
              console.log('Transaction confirmed!');
              break;
            }
          }
          await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
          retries++;
        } catch (err) {
          if (err.message && err.message.includes('Transaction failed')) {
            throw err;
          }
          // Continue retrying
          await new Promise(resolve => setTimeout(resolve, 1000));
          retries++;
        }
      }
      
      if (!confirmed) {
        throw new Error('Transaction confirmation timeout');
      }

      console.log('Sending to backend:', { ca: ca.trim(), payment_tx_sig: signature, wallet_address: publicKey.toString() });
      // Create link with payment transaction signature
      const response = await axios.post(`${API_URL}/api/create`, {
        ca: ca.trim(),
        payment_tx_sig: signature,
        wallet_address: publicKey.toString(),
      });
      console.log('Backend response:', response.data);

      const fullUrl = `${window.location.origin}/ca/${response.data.uuid}`;
      setLink(fullUrl);
      setCa('');
      loadTopCA(); // Refresh top CA list
    } catch (err) {
      console.error('Error:', err);
      setError(
        err.response?.data?.error || err.message || 'Failed to create link. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(link);
    alert('Link copied to clipboard!');
  };

  const copyItemUrl = (uuid) => {
    const itemUrl = `${window.location.origin}/ca/${uuid}`;
    navigator.clipboard.writeText(itemUrl);
    setCopiedUuid(uuid);
    setTimeout(() => {
      setCopiedUuid(null);
    }, 2000);
  };

  const formatNumber = (num) => {
    if (!num || num === 0) return 'N/A';
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    if (num >= 1e3) return `$${(num / 1e3).toFixed(2)}K`;
    return `$${num.toFixed(2)}`;
  };

  const formatPercent = (num) => {
    if (num === null || num === undefined) return 'N/A';
    const sign = num >= 0 ? '+' : '';
    return `${sign}${num.toFixed(2)}%`;
  };

  const getPercentColor = (num) => {
    if (num === null || num === undefined) return '#888';
    return num >= 0 ? '#10b981' : '#ef4444';
  };

  return (
    <div className="home-container">
      {/* Header */}
      <header className="header">
        <div className="header-content">
          <div className="logo">
            <Shuffle
              text="Copy CA"
              shuffleDirection="right"
              duration={0.35}
              animationMode="evenodd"
              shuffleTimes={1}
              ease="power3.out"
              stagger={0.03}
              threshold={0.1}
              triggerOnce={true}
              triggerOnHover
              respectReducedMotion={true}
              loop={true}
              loopDelay={3}
              tag="h1"
              className="logo-shuffle"
            />
          </div>
          <div className="header-nav">
            {connected && publicKey && features.my_ca_enabled && (
              <a href={`/my-links/${publicKey.toString()}`} className="my-links-link">
                My Links
              </a>
            )}
            <div className="wallet-header">
              <WalletMultiButton />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="main-content">
        {/* Create Link Section */}
        <section className="create-section">
          <div className="create-card">
            <h2 className="section-title">Create Secure Share Link</h2>
            <p className="section-subtitle">Protect your CA address from snipers and bots</p>

            {paymentConfig && paymentConfig.enabled === false ? (
              <div className="coming-soon">
                <p className="coming-soon-text">Coming Soon</p>
                <p className="coming-soon-subtitle">Payment feature will be available soon</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="form">
                <div className="input-group">
                  <input
                    type="text"
                    value={ca}
                    onChange={(e) => setCa(e.target.value)}
                    placeholder="Enter Solana CA address"
                    className="input"
                    disabled={loading || !connected}
                  />
                </div>

                {error && <div className="error">{error}</div>}

                <button
                  type="submit"
                  className="button"
                  disabled={loading || !ca.trim() || !connected || !paymentConfig || paymentConfig.enabled === false}
                >
                  {loading ? 'Processing payment...' : 'Create Link'}
                </button>
                {paymentConfig && paymentConfig.enabled !== false && (
                  <p className="payment-info">
                    Cost: {parseInt(paymentConfig.amount) / 1000}k{' '}
                    <a
                      href={`https://pump.fun/${paymentConfig.token_mint}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="token-link"
                    >
                      $COPYCA
                    </a>
                  </p>
                )}
              </form>
            )}

            {link && (
              <div className="link-result">
                <p className="link-label">Your short link:</p>
                <div className="link-box">
                  <a href={link} target="_blank" rel="noopener noreferrer" className="link">
                    {link}
                  </a>
                  <button onClick={copyToClipboard} className="copy-button">
                    Copy
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Description Section */}
        <section className="description-section">
          <div className="description-card">
            <h2 className="section-title">Protect Your CA from Snipers</h2>
            <p className="description-text">
              CopyCA helps you safely share Solana Contract Addresses in messengers and social networks 
              without exposing them to automated snipers and bots that can buy tokens before others.
            </p>
            
            <div className="benefits-list">
              <div className="benefit-item">
                <div className="benefit-number">1</div>
                <div className="benefit-content">
                  <h3>Safe Sharing in Messengers</h3>
                  <p>Share your CA address in Telegram, Discord, WhatsApp, and other messengers without worrying about bots scraping and buying tokens before your community</p>
                </div>
              </div>
              
              <div className="benefit-item">
                <div className="benefit-number">2</div>
                <div className="benefit-content">
                  <h3>Social Media Protection</h3>
                  <p>Post links on Twitter, Facebook, Instagram, and other platforms. Bots can't automatically access the CA address - they need to pass captcha verification first</p>
                </div>
              </div>
              
              <div className="benefit-item">
                <div className="benefit-number">3</div>
                <div className="benefit-content">
                  <h3>Captcha Barrier</h3>
                  <p>Every visitor must complete Cloudflare Turnstile captcha before seeing the CA address. This prevents automated snipers from accessing your token address instantly</p>
                </div>
              </div>
              
              <div className="benefit-item">
                <div className="benefit-number">4</div>
                <div className="benefit-content">
                  <h3>Fair Distribution</h3>
                  <p>Give your community equal opportunity to buy tokens. No more front-running by bots that monitor public channels and social media for new CA addresses</p>
                </div>
              </div>
            </div>
            
            <div className="features">
              <div className="feature">
                <FiLink className="feature-icon" />
                <h3>Secure Sharing</h3>
                <p>Create short links that hide your CA address until verification</p>
              </div>
              <div className="feature">
                <FiTrendingUp className="feature-icon" />
                <h3>Anti-Sniper Protection</h3>
                <p>Captcha verification prevents bots from accessing CA addresses automatically</p>
              </div>
              <div className="feature">
                <FiEye className="feature-icon" />
                <h3>Safe Distribution</h3>
                <p>Share in Telegram, Discord, Twitter without fear of snipers</p>
              </div>
            </div>
          </div>
        </section>

        {/* Top CA and Latest CA Section */}
        {(features.top_ca_enabled || features.last_ca_enabled) && (
          <section className="top-section">
            <div className={`top-section-grid ${features.top_ca_enabled && features.last_ca_enabled ? 'two-columns' : 'one-column'}`}>
              {/* Top CA Column */}
              {features.top_ca_enabled && (
                <div className="top-card">
                  <h2 className="section-title">Top 10 Most Viewed CA</h2>
            {loadingTop ? (
              <div className="loading">Loading...</div>
            ) : topCA.length === 0 ? (
              <div className="empty-state">No CA addresses yet</div>
            ) : (
              <div className="top-list">
                {topCA.map((item, index) => (
                  <div key={item.uuid} className="top-item">
                    <div className="top-item-rank">#{index + 1}</div>
                    <div className="top-item-content">
                      <div className="top-item-header">
                        <div className="top-item-title-group">
                          {item.logo_url && (
                            <div className="top-item-logo">
                              <img
                                src={item.logo_url}
                                alt={item.name || item.symbol || 'Token logo'}
                              />
                            </div>
                          )}
                          <h3 className="top-item-name">
                            {item.name || item.symbol || 'Unknown Token'}
                            {item.symbol && <span className="top-item-symbol">({item.symbol})</span>}
                          </h3>
                        </div>
                      </div>
                      <div className="top-item-details">
                        <div className="top-item-detail">
                          <span className="detail-label">Views:</span>
                          <span className="detail-value">{item.views}</span>
                        </div>
                        {item.market_cap > 0 && (
                          <div className="top-item-detail">
                            <span className="detail-label">Market Cap:</span>
                            <span className="detail-value">{formatNumber(item.market_cap)}</span>
                          </div>
                        )}
                        {item.price_change_24h !== undefined && (
                          <div className="top-item-detail">
                            <span className="detail-label">24h Change:</span>
                            <span 
                              className="detail-value"
                              style={{ color: getPercentColor(item.price_change_24h) }}
                            >
                              {formatPercent(item.price_change_24h)}
                            </span>
                          </div>
                        )}
                      </div>
                      {(item.website_url || item.twitter_url) && (
                        <div className="top-item-links">
                          {item.website_url && (
                            <a
                              href={item.website_url}
                              className="top-item-link"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {item.website_label || 'Website'}
                            </a>
                          )}
                          {item.twitter_url && (
                            <a
                              href={item.twitter_url}
                              className="top-item-link"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Twitter
                            </a>
                          )}
                        </div>
                      )}
                      <div className="top-item-ca">
                        <code>{item.CA}</code>
                      </div>
                      <div className="top-item-actions">
                        <a 
                          href={`/ca/${item.uuid}`} 
                          className="open-button"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <FiExternalLink /> Open
                        </a>
                        <button
                          type="button"
                          className={`open-button copy-url-button ${copiedUuid === item.uuid ? 'copied' : ''}`}
                          onClick={() => copyItemUrl(item.uuid)}
                        >
                          {copiedUuid === item.uuid ? (
                            <>
                              <FiCheck /> Copied!
                            </>
                          ) : (
                            <>
                              <FiCopy /> Copy URL
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                  ))}
                </div>
              )}
                </div>
              )}

              {/* Latest CA Column */}
              {features.last_ca_enabled && (
                <div className="top-card">
                  <h2 className="section-title">Last 10 Links</h2>
              {loadingLatest ? (
                <div className="loading">Loading...</div>
              ) : latestCA.length === 0 ? (
                <div className="empty-state">No CA addresses yet</div>
              ) : (
                <div className="top-list">
                  {latestCA.map((item, index) => (
                    <div key={item.uuid} className="top-item">
                      <div className="top-item-rank">#{index + 1}</div>
                      <div className="top-item-content">
                        <div className="top-item-header">
                          <div className="top-item-title-group">
                            {item.logo_url && (
                              <div className="top-item-logo">
                                <img
                                  src={item.logo_url}
                                  alt={item.name || item.symbol || 'Token logo'}
                                />
                              </div>
                            )}
                            <h3 className="top-item-name">
                              {item.name || item.symbol || 'Unknown Token'}
                              {item.symbol && <span className="top-item-symbol">({item.symbol})</span>}
                            </h3>
                          </div>
                        </div>
                        <div className="top-item-details">
                          <div className="top-item-detail">
                            <span className="detail-label">Views:</span>
                            <span className="detail-value">{item.views}</span>
                          </div>
                          {item.market_cap > 0 && (
                            <div className="top-item-detail">
                              <span className="detail-label">Market Cap:</span>
                              <span className="detail-value">{formatNumber(item.market_cap)}</span>
                            </div>
                          )}
                          {item.price_change_24h !== undefined && (
                            <div className="top-item-detail">
                              <span className="detail-label">24h Change:</span>
                              <span 
                                className="detail-value"
                                style={{ color: getPercentColor(item.price_change_24h) }}
                              >
                                {formatPercent(item.price_change_24h)}
                              </span>
                            </div>
                          )}
                        </div>
                        {(item.website_url || item.twitter_url) && (
                          <div className="top-item-links">
                            {item.website_url && (
                              <a
                                href={item.website_url}
                                className="top-item-link"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {item.website_label || 'Website'}
                              </a>
                            )}
                            {item.twitter_url && (
                              <a
                                href={item.twitter_url}
                                className="top-item-link"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Twitter
                              </a>
                            )}
                          </div>
                        )}
                        <div className="top-item-ca">
                          <code>{item.CA}</code>
                        </div>
                        <div className="top-item-actions">
                          <a 
                            href={`/ca/${item.uuid}`} 
                            className="open-button"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FiExternalLink /> Open
                          </a>
                          <button
                            type="button"
                            className={`open-button copy-url-button ${copiedUuid === item.uuid ? 'copied' : ''}`}
                            onClick={() => copyItemUrl(item.uuid)}
                          >
                            {copiedUuid === item.uuid ? (
                              <>
                                <FiCheck /> Copied!
                              </>
                            ) : (
                              <>
                                <FiCopy /> Copy URL
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
                </div>
              )}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

export default Home;
